import React, {useEffect, useState, useMemo} from 'react';
import {
    Card,
    Table,
    Tag,
    Button,
    Space,
    Input,
    Modal,
    Form,
    message,
    Tooltip,
    Select,
    Checkbox,
    Dropdown,
    Menu,
    Typography,
    Alert,
    Popconfirm,
    Switch,
    Tabs
} from 'antd';
import {
    EditOutlined,
    LinkOutlined,
    SearchOutlined,
    SettingOutlined,
    ReloadOutlined,
    PlusOutlined,
    ExportOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import {apiService} from '../../../services/apiService';

const {Search} = Input;

interface ColumnConfig {
    visible: boolean;
    order: number;
}

interface ColumnSettings {
    storeCode: ColumnConfig;
    status: ColumnConfig;
    tags: ColumnConfig;
    warehouseIds: ColumnConfig;
    customerIds: ColumnConfig;
    apiKey: ColumnConfig;
    privateKey: ColumnConfig;
    lastConnected: ColumnConfig;
    createdAt: ColumnConfig;
    actions: ColumnConfig;
}

interface HKTVAccount {
    id: string;
    storeCode: string;
    status: 'connected' | 'disconnected';
    apiKey?: string;
    privateKey?: string;
    warehouseIds?: string;
    customerIds?: string; // 预定入库的客户IDs（逗号分隔）
    tags?: string[];
    warehouseTags?: Record<string, string[]>; // 每個 warehouse ID 對應的 tags
    isUpdateStore: boolean;
  isEnabled?: boolean;
    mmsLogin?: string;
    mmsPassword?: string;
    lastConnected?: string;
    createdAt?: string;
    updatedAt?: string;
}

// 新增標籤選項介面
interface TagOption {
    tagName: string;
}

type TableRow = HKTVAccount;

const HKTVmallAdminAccounts: React.FC = () => {
    const [accounts, setAccounts] = useState<HKTVAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editingAccount, setEditingAccount] = useState<HKTVAccount | null>(null);
    const [searchText, setSearchText] = useState('');
    const [filteredAccounts, setFilteredAccounts] = useState<HKTVAccount[]>([]);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 10 });
    // 新增標籤選項狀態
    const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
    // 新增客户列表状态
    const [customers, setCustomers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
    // 列配置狀態 - 參考 Inbound 頁面實現
    const [columnSettings, setColumnSettings] = useState<ColumnSettings>(() => {
        const saved = localStorage.getItem('hktv-accounts-columns-config');
        const version = localStorage.getItem('hktv-accounts-columns-version');

        const defaultConfig = {
            storeCode: {visible: true, order: 0},
            status: {visible: true, order: 1},
            warehouseIds: {visible: true, order: 2},
            customerIds: {visible: true, order: 3},
            tags: {visible: true, order: 4},
            apiKey: {visible: false, order: 5},
            privateKey: {visible: false, order: 6},
            lastConnected: {visible: true, order: 7},
            createdAt: {visible: false, order: 8},
            actions: {visible: true, order: 9}
        } as ColumnSettings;

        // 檢查版本，如果不是最新版本則重置配置
        if (version !== '2025-11-v3' || !saved) {
            localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(defaultConfig));
            localStorage.setItem('hktv-accounts-columns-version', '2025-11-v3');
            return defaultConfig;
        }

        try {
            const parsedConfig = JSON.parse(saved);

            // 檢查並清理舊的 merchantCode 配置
            if (parsedConfig.merchantCode) {
                delete parsedConfig.merchantCode;
            }

            // 確保所有必要的字段都存在，如果缺少則使用默認值
            const cleanConfig: ColumnSettings = {
                storeCode: parsedConfig.storeCode || defaultConfig.storeCode,
                status: parsedConfig.status || defaultConfig.status,
                tags: parsedConfig.tags || defaultConfig.tags,
                warehouseIds: parsedConfig.warehouseIds || defaultConfig.warehouseIds,
                customerIds: parsedConfig.customerIds || defaultConfig.customerIds,
                apiKey: parsedConfig.apiKey || defaultConfig.apiKey,
                privateKey: parsedConfig.privateKey || defaultConfig.privateKey,
                lastConnected: parsedConfig.lastConnected || defaultConfig.lastConnected,
                createdAt: parsedConfig.createdAt || defaultConfig.createdAt,
                actions: parsedConfig.actions || defaultConfig.actions
            };

            // 保存清理後的配置
            localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(cleanConfig));
            return cleanConfig;
        } catch {
            localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(defaultConfig));
            localStorage.setItem('hktv-accounts-columns-version', '2025-11-v3');
            return defaultConfig;
        }
    });
    const [form] = Form.useForm();
    const [addForm] = Form.useForm();

    // 初始化數據
    useEffect(() => {
        fetchAccounts();
        fetchTagOptions(); // 取得標籤選項
        fetchCustomers(); // 取得客户列表
    }, []);

    // 過濾數據
    useEffect(() => {
        const filtered = accounts.filter(account => {
            const matchesSearch = account.storeCode.toLowerCase().includes(searchText.toLowerCase());
            const matchesStatus = !statusFilter || account.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
        setFilteredAccounts(filtered);
    }, [accounts, searchText, statusFilter]);

    const tableData: TableRow[] = filteredAccounts;

    useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [filteredAccounts.length]);

    const handleTableChange = (pageInfo: TablePaginationConfig) => {
        setPagination(prev => ({
            current: pageInfo.current || prev.current,
            pageSize: pageInfo.pageSize || prev.pageSize
        }));
    };

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (searchText) {
                params.search = searchText;
            }
            if (statusFilter) {
                params.status = statusFilter;
            }
            const qp = new URLSearchParams(params).toString();
            const result: any = await apiService.get(`/integrations/hktvmall/accounts${qp ? `?${qp}` : ''}`);
            if (result?.success) {
                setAccounts(result.data || []);
            } else {
                message.error(result?.message || '獲取帳號列表失敗');
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            message.error('獲取帳號列表失敗');
        } finally {
            setLoading(false);
        }
    };

    // 取得標籤選項
    const fetchTagOptions = async () => {
        try {
            const result: any = await apiService.listHKTVRunTags();
            if (result?.success) {
                const options = result.data.map((tag: any) => ({
                    tagName: tag.tagName
                }));
                setTagOptions(options);
            }
        } catch (error) {
            console.error('Error fetching tag options:', error);
        }
    };

    // 取得客户列表
    const fetchCustomers = async () => {
        try {
            // Remove hardcoded companyId to show customers from all companies the user has access to
            // Remove limit to show all active customers
            const res: any = await apiService.getCustomersV2({ limit: 10000, isActive: true });
            const list: any[] = res?.customers || res?.data || [];
            const mapped = list.map((c: any) => ({
                id: c.id,
                name: c.name,
                email: c.email || ''
            }));
            setCustomers(mapped);
            console.log('✅ Loaded customers for HKTV accounts:', mapped.length);
        } catch (error) {
            console.error('❌ Failed to load customers:', error);
            setCustomers([]);
        }
    };

    /**
     * Ask before a save REMOVES any tag, listing exactly what would go.
     *
     * Removing a run tag silently changes which orders get picked: a store dropped
     * from a tag disappears from that pick run entirely, with no error anywhere. On
     * 2026-10-01 H779200101 and H779200401 lost `Dry SD` through this form and were
     * out of the Dry SD run for over two days before anyone noticed.
     *
     * Additions need no confirmation — they are visible and harmless. Only removals
     * are destructive, so only removals interrupt.
     *
     * Resolves true when there is nothing to remove, so the happy path never stops.
     */
    const confirmTagRemovals = (
        before: Record<string, string[]> | undefined,
        after: Record<string, string[]>
    ): Promise<boolean> => {
        const removals: string[] = [];
        Object.keys(after).forEach(warehouseId => {
            const had = before?.[warehouseId] || [];
            const now = after[warehouseId] || [];
            const gone = had.filter(t => !now.includes(t));
            if (gone.length > 0) {
                removals.push(`倉庫 ${warehouseId}：移除 ${gone.join('、')}`);
            }
        });

        if (removals.length === 0) return Promise.resolve(true);

        return new Promise<boolean>(resolve => {
            Modal.confirm({
                title: '確認移除標籤？',
                width: 520,
                content: (
                    <div>
                        <p>此次儲存將移除以下標籤：</p>
                        <ul>
                            {removals.map(line => <li key={line}>{line}</li>)}
                        </ul>
                        <p style={{ marginBottom: 0 }}>
                            移除標籤會令該倉庫不再出現在對應的揀貨批次中，系統不會另行提示。
                        </p>
                    </div>
                ),
                okText: '確認移除',
                okButtonProps: { danger: true },
                cancelText: '取消',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    };

    /**
     * Warehouses whose tag field this modal actually populated from the server.
     *
     * The save used to send `values['tags_'+wh] || []` for EVERY warehouse in the ID
     * list, so a field that never loaded was indistinguishable from one the user
     * emptied on purpose — and an empty list is applied destructively. That is how
     * H779200101 and H779200401 lost `Dry SD` on 2026-10-01 and dropped out of the
     * Dry SD pick run for over two days without anything erroring.
     *
     * Tracking what we populated lets the payload stay silent about a warehouse we
     * know nothing about, which the backend then leaves alone.
     */
    const [managedWarehouses, setManagedWarehouses] = useState<string[]>([]);

    const handleEdit = (record: HKTVAccount) => {
        setEditingAccount(record);
        
        // 準備初始值
        const initialValues: any = {
            apiKey: record.apiKey,
            privateKey: record.privateKey,
            warehouseIds: record.warehouseIds,
            tags: Array.isArray(record.tags) ? record.tags : [],
            customerIds: record.customerIds ? record.customerIds.split(',').map(id => id.trim()).filter(Boolean) : [],
            mmsLogin: record.mmsLogin || '',
            mmsPassword: record.mmsPassword || '',
        };
        
        // 如果有 warehouse IDs，為每個 warehouse 設置 tags
        const populated: string[] = [];
        if (record.warehouseIds) {
            const warehouseList = record.warehouseIds.split(',').map(id => id.trim()).filter(Boolean);
            warehouseList.forEach(warehouseId => {
                // Only claim to manage a warehouse when the server actually told us its
                // tags. A missing warehouseTags entry means we do not know its state, so
                // the save must not speak for it.
                if (record.warehouseTags && Object.prototype.hasOwnProperty.call(record.warehouseTags, warehouseId)) {
                    initialValues[`tags_${warehouseId}`] = record.warehouseTags[warehouseId] || [];
                    populated.push(warehouseId);
                }
            });
        }
        setManagedWarehouses(populated);
        
        form.setFieldsValue(initialValues);
        setEditModalVisible(true);
    };

    const handleTestConnection = async (record: HKTVAccount) => {
        setLoading(true);
        try {
            const result: any = await apiService.post(`/integrations/hktvmall/accounts/${record.storeCode}/test`, {});
            if (result?.success) {
                message.success(`${record.storeCode} 連線測試成功`);
            } else {
                message.error(`${record.storeCode} 連線測試失敗：${result?.message}`);
            }
            // 重新拉取，僅以後端回傳時間為準
            await fetchAccounts();
        } catch (error) {
            console.error('Connection test error:', error);
            message.error('連線測試失敗');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEdit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 準備提交的數據
            const submitData: any = {
                apiKey: values.apiKey,
                privateKey: values.privateKey,
                warehouseIds: values.warehouseIds,
                customerIds: Array.isArray(values.customerIds)
                    ? values.customerIds.join(',')
                    : (values.customerIds || ''),
                mmsLogin: values.mmsLogin || null,
                mmsPassword: values.mmsPassword || null,
            };

            // 判斷是否有 warehouse IDs
            const warehouseList = values.warehouseIds 
                ? values.warehouseIds.split(',').map((id: string) => id.trim()).filter(Boolean)
                : [];

            if (warehouseList.length > 0) {
                // Send ONLY the warehouses this modal can speak for: ones whose tags it
                // loaded from the server, plus ones the user actively gave a value to
                // (a warehouse typed in during this edit). A warehouse we never
                // populated is omitted entirely, and the backend leaves it untouched —
                // an omitted warehouse now means "unchanged", where an empty array
                // still means "clear it".
                const warehouseTags: Record<string, string[]> = {};
                warehouseList.forEach((warehouseId: string) => {
                    const tagsKey = `tags_${warehouseId}`;
                    const raw = values[tagsKey];
                    const canSpeakFor = managedWarehouses.includes(warehouseId) || raw !== undefined;
                    if (canSpeakFor) {
                        warehouseTags[warehouseId] = (raw || []).filter((t: string) => !!t);
                    }
                });
                submitData.warehouseTags = warehouseTags;

                // The handler's finally{} clears the loading flag on this return path.
                const proceed = await confirmTagRemovals(editingAccount?.warehouseTags, warehouseTags);
                if (!proceed) return;
            } else {
                // 沒有 warehouse IDs - tags 屬於 store code
                submitData.tags = (values.tags || []).filter((t: string) => !!t);
            }

            const result: any = await apiService.put(`/integrations/hktvmall/accounts/${editingAccount?.id}`, submitData);

            if (result?.success) {
                // 重新獲取完整的帳號列表以確保 tags 和 warehouseTags 數據正確
                await fetchAccounts();

                // 重新獲取標籤選項以確保數據一致性
                fetchTagOptions();

                message.success('帳號信息已更新');
                setEditModalVisible(false);
                setEditingAccount(null);
                form.resetFields();
            } else {
                message.error(result?.message || '更新失敗');
            }
        } catch (error) {
            console.error('Update error:', error);
            message.error('更新失敗');
        } finally {
            setLoading(false);
        }
    };

    // 列設置功能 - 參考 Inbound 頁面實現
    const handleColumnVisibilityChange = (columnKey: keyof ColumnSettings, visible: boolean) => {
        const newSettings = {
            ...columnSettings,
            [columnKey]: {...columnSettings[columnKey], visible}
        };
        setColumnSettings(newSettings);
        localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(newSettings));
    };

    const showAllColumns = () => {
        const newSettings = Object.keys(columnSettings).reduce((acc, key) => ({
            ...acc,
            [key]: {...columnSettings[key as keyof ColumnSettings], visible: true}
        }), {} as ColumnSettings);
        setColumnSettings(newSettings);
        localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(newSettings));
    };

    const resetColumnSettings = () => {
        const defaultConfig: ColumnSettings = {
            storeCode: {visible: true, order: 0},
            status: {visible: true, order: 1},
            warehouseIds: {visible: true, order: 2},
            customerIds: {visible: true, order: 3},
            tags: {visible: true, order: 4},
            apiKey: {visible: false, order: 5},
            privateKey: {visible: false, order: 6},
            lastConnected: {visible: true, order: 7},
            createdAt: {visible: false, order: 8},
            actions: {visible: true, order: 9}
        };
        setColumnSettings(defaultConfig);
        localStorage.setItem('hktv-accounts-columns-config', JSON.stringify(defaultConfig));
        localStorage.setItem('hktv-accounts-columns-version', '2025-11-v3');
    };

    // 新增賬號功能
    const handleAddAccount = () => {
        addForm.resetFields();
        setAddModalVisible(true);
    };

    const handleSaveAdd = async () => {
        try {
            const values = await addForm.validateFields();
            setLoading(true);

            // 準備提交的數據
            const submitData: any = {
                storeCode: values.storeCode,
                apiKey: values.apiKey,
                privateKey: values.privateKey,
                warehouseIds: values.warehouseIds,
                customerIds: Array.isArray(values.customerIds) 
                    ? values.customerIds.join(',') 
                    : (values.customerIds || ''),
            };

            // 判斷是否有 warehouse IDs
            const warehouseList = values.warehouseIds 
                ? values.warehouseIds.split(',').map((id: string) => id.trim()).filter(Boolean)
                : [];

            if (warehouseList.length > 0) {
                // 有 warehouse IDs - 收集每個 warehouse 的 tags
                const warehouseTags: Record<string, string[]> = {};
                warehouseList.forEach((warehouseId: string) => {
                    const tagsKey = `tags_${warehouseId}`;
                    warehouseTags[warehouseId] = (values[tagsKey] || []).filter((t: string) => !!t);
                });
                submitData.warehouseTags = warehouseTags;
            } else {
                // 沒有 warehouse IDs - tags 屬於 store code
                submitData.tags = (values.tags || []).filter((t: string) => !!t);
            }

            const result: any = await apiService.post('/integrations/hktvmall/accounts', submitData);

            if (result?.success) {
                message.success('新賬號創建成功');
                setAddModalVisible(false);
                addForm.resetFields();
                // 分組功能已移除
                // 重新加載賬號列表
                fetchAccounts();
                
                // 重新獲取標籤選項以確保數據一致性
                fetchTagOptions();
            } else {
                message.error(result?.message || '創建失敗');
            }
        } catch (error: any) {
            console.error('Create error:', error);
            // 提取並顯示後端返回的錯誤消息
            const errorMessage = error?.response?.data?.message 
                || error?.response?.data?.error 
                || error?.message 
                || '創建失敗';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // 刪除賬號功能
    const handleDeleteAccount = async (record: HKTVAccount) => {
        try {
            setLoading(true);

            const result: any = await apiService.delete(`/integrations/hktvmall/accounts/${record.id}`);

            if (result?.success) {
                message.success(`賬號 ${record.storeCode} 已成功刪除`);
                // 重新加載賬號列表
                fetchAccounts();
            } else {
                message.error(result?.message || '刪除失敗');
            }
        } catch (error) {
            console.error('Delete error:', error);
            message.error('刪除失敗');
        } finally {
            setLoading(false);
        }
    };

    // 新增：調用後端代理的 /oapi/api/store
    const handleToggleStoreApi = async (checked: boolean, record: HKTVAccount) => {
        setLoading(true);
        try {
            const res: any = await apiService.put(`/integrations/hktvmall/accounts/${record.id}/is-update-store`, {isUpdateStore: checked});
            if (res?.success) {
                setAccounts(prev => prev.map(acc => acc.id === record.id ? { ...acc, isUpdateStore: checked } : acc));
                message.success('已更新「是否更新商店」設定');
            } else {
                message.error(res?.message || '更新失敗');
            }
        } catch {
            message.error('更新失敗');
        } finally {
            setLoading(false);
        }
    };

  // 啟用/停用帳號
  const handleToggleEnabled = async (checked: boolean, record: HKTVAccount) => {
    setLoading(true);
    try {
      const result: any = await apiService.put(`/integrations/hktvmall/accounts/${record.id}/is-enabled`, { isEnabled: checked });
      if (result?.success) {
        setAccounts(prev => prev.map(acc => acc.id === record.id ? { ...acc, isEnabled: checked } : acc));
        message.success(`${record.storeCode} 已${checked ? '啟用' : '停用'}`);
      } else {
        message.error(result?.message || '操作失敗');
      }
    } catch (e) {
      message.error('操作失敗');
    } finally {
      setLoading(false);
    }
  };

    const toggleAllEnabled = async (enabled: boolean) => {
        if (!accounts.length) return;
        setLoading(true);
        try {
            await Promise.all(accounts.map(acc =>
                apiService.put(`/integrations/hktvmall/accounts/${acc.id}/is-enabled`, { isEnabled: enabled })
            ));
            setAccounts(prev => prev.map(acc => ({ ...acc, isEnabled: enabled })));
            message.success(`已${enabled ? '啟用' : '停用'}全部帳號`);
        } catch (error) {
            console.error('bulk toggle enabled error:', error);
            message.error('批次更新啟用狀態失敗');
        } finally {
            setLoading(false);
        }
    };

    const toggleAllUpdateStore = async (checked: boolean) => {
        if (!accounts.length) return;
        setLoading(true);
        try {
            await Promise.all(accounts.map(acc =>
                apiService.put(`/integrations/hktvmall/accounts/${acc.id}/is-update-store`, { isUpdateStore: checked })
            ));
            setAccounts(prev => prev.map(acc => ({ ...acc, isUpdateStore: checked })));
            message.success(`已${checked ? '開啟' : '關閉'}全部「是否更新商店」`);
        } catch (error) {
            console.error('bulk toggle update store error:', error);
            message.error('批次更新「是否更新商店」失敗');
        } finally {
            setLoading(false);
        }
    };

    const allEnabled = useMemo(() => accounts.every(acc => acc.isEnabled !== false), [accounts]);
    const allUpdateStore = useMemo(() => accounts.every(acc => !!acc.isUpdateStore), [accounts]);

    const testAllConnections = async () => {
        if (!accounts.length) return;
        setLoading(true);
        let successCount = 0;
        let failCount = 0;
        try {
            for (const acc of accounts) {
                try {
                    const result: any = await apiService.post(`/integrations/hktvmall/accounts/${acc.storeCode}/test`, {});
                    if (result?.success) {
                        successCount += 1;
                    } else {
                        failCount += 1;
                    }
                } catch (err) {
                    failCount += 1;
                }
            }
            await fetchAccounts();
            if (failCount === 0) {
                message.success(`連線測試完成，全部 ${successCount} 個帳號皆成功`);
            } else {
                message.warning(`連線測試完成：成功 ${successCount}，失敗 ${failCount}`);
            }
        } catch (err) {
            message.error('批次測試連線時發生錯誤');
        } finally {
            setLoading(false);
        }
    };

    // 導出功能 - 參考 Inbound 頁面實現
    const handleExportCSV = () => {
        try {
            // 生成CSV內容
            const csvContent = generateCSVContent(filteredAccounts);

            // 創建Blob並下載
            const blob = new Blob(['\uFEFF' + csvContent], {type: 'text/csv;charset=utf-8;'});
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `hktvmall_accounts_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success('HKTVmall 帳號數據已成功導出');
        } catch (error) {
            console.error('導出失敗:', error);
            message.error('導出失敗，請稍後重試');
        }
    };

    // 生成CSV內容
    const generateCSVContent = (data: HKTVAccount[]) => {
        const headers = [
            'Store Code', 'Status', 'API Key', 'Private Key', 'Warehouse IDs',
            'Last Connected', 'Created At', 'Updated At'
        ];

        const rows = data.map(item => [
            item.storeCode || '',
            item.status || '',
            item.apiKey || '',
            item.privateKey || '',
            item.warehouseIds || '',
            item.lastConnected ? `\u200B${new Date(item.lastConnected).toISOString().split('T')[0]}` : '',
            item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
            item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : ''  // updatedAt - 如果有的話
        ]);

        return [headers, ...rows].map(row =>
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`)
        ).map(row => row.join(',')).join('\n');
    };

    // 建立標籤選項
    const tagOptionsForSelect = tagOptions.map(option => (
        <Select.Option key={option.tagName} value={option.tagName}>
            {option.tagName}
        </Select.Option>
    ));

    // Use useMemo to ensure columns re-render when customers state changes
    const allColumns: ColumnsType<TableRow> = useMemo(() => [
        {
            title: 'Store Code',
            dataIndex: 'storeCode',
            key: 'storeCode',
            sorter: (a, b) => a.storeCode.localeCompare(b.storeCode),
            width: 80,
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            render: (val: string[] | undefined, record: TableRow) => (
                'isGroup' in record ? null : (
                    (val && val.length > 0) ? (
                        <Space size={4} wrap>
                            {val.map((t, idx) => <Tag key={idx} style={{marginInlineEnd: 4}}>{t}</Tag>)}
                        </Space>
                    ) : <span style={{color: '#999'}}>-</span>
                )
            ),
            width: 120,
        },


        {
            title: '倉庫IDs',
            dataIndex: 'warehouseIds',
            key: 'warehouseIds',
            render: (val: string, record: TableRow) => ('isGroup' in record ? null : (val ||
                <span style={{color: '#999'}}>-</span>)),
            width: 180,
        },
        {
            title: '客戶',
            dataIndex: 'customerIds',
            key: 'customerIds',
            render: (val: string, record: TableRow) => {
                if ('isGroup' in record) return null;
                if (!val) return <span style={{color: '#999'}}>-</span>;
                
                const customerIds = val.split(',').map(id => id.trim()).filter(Boolean);
                const customerNames = customerIds.map(id => {
                    const customer = customers.find(c => c.id === id);
                    return customer ? customer.name : id;
                });
                
                if (customerNames.length === 0) {
                    return <span style={{color: '#999'}}>-</span>;
                }
                
                return (
                    <Space size={4} wrap>
                        {customerNames.map((name, idx) => (
                            <Tag key={idx} color="blue" style={{marginInlineEnd: 4}}>
                                {name}
                            </Tag>
                        ))}
                    </Space>
                );
            },
            width: 200,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string, record: TableRow) => (
                'isGroup' in record ? null : (
                    <Tag
                        icon={status === 'connected' ? <CheckCircleOutlined/> : <CloseCircleOutlined/>}
                        color={status === 'connected' ? 'green' : 'default'}
                    >
                        {status === 'connected' ? '已連線' : '未連線'}
                    </Tag>
                )
            ),
            filters: [
                {text: '已連線', value: 'connected'},
                {text: '未連線', value: 'disconnected'},
            ],
            onFilter: (value, record: TableRow) => ('isGroup' in record ? false : record.status === value),
            width: 80,
        },
        {
            title: 'API Key',
            dataIndex: 'apiKey',
            key: 'apiKey',
            render: (apiKey: string, record: TableRow) => {
                if ('isGroup' in record) return null;
                if (!apiKey) return <span style={{color: '#999'}}>未設置</span>;
                const displayText = apiKey.length > 20 ? `${apiKey.substring(0, 20)}...` : apiKey;
                return (
                    <Tooltip title={apiKey} placement="topLeft">
            <span
                style={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    cursor: 'pointer',
                    color: '#1890ff'
                }}
            >
              {displayText}
            </span>
                    </Tooltip>
                );
            },
            width: 160,
        },
        {
            title: 'Private Key',
            dataIndex: 'privateKey',
            key: 'privateKey',
            render: (privateKey: string, record: TableRow) => {
                if ('isGroup' in record) return null;
                if (!privateKey) return <span style={{color: '#999'}}>未設置</span>;
                const displayText = privateKey.length > 20 ? `${privateKey.substring(0, 20)}...` : privateKey;
                return (
                    <Tooltip title={
                        <div style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            maxWidth: '400px'
                        }}>
                            {privateKey}
                        </div>
                    } placement="topLeft">
            <span
                style={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    cursor: 'pointer',
                    color: '#1890ff'
                }}
            >
              {displayText}
            </span>
                    </Tooltip>
                );
            },
            width: 160,
        },
        {
            title: 'Last Connected',
            dataIndex: 'lastConnected',
            key: 'lastConnected',
            sorter: (a, b) => {
                const aTs = Date.parse((a as HKTVAccount).lastConnected || '') || 0;
                const bTs = Date.parse((b as HKTVAccount).lastConnected || '') || 0;
                return aTs - bTs;
            },
            render: (lastConnected: string) => {
                const ts = Date.parse(lastConnected || '');
                if (!lastConnected || Number.isNaN(ts)) {
                    return <span style={{fontSize: '12px', color: '#999'}}>從未連線</span>;
                }
                return (
                    <span style={{fontSize: '12px'}}>
            {new Date(ts).toLocaleString('zh-TW', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false
            })}
          </span>
                );
            },
            width: 80,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => ('isGroup' in a ? '' : (a as HKTVAccount).createdAt || '').localeCompare('isGroup' in b ? '' : (b as HKTVAccount).createdAt || ''),
            render: (createdAt: string, record: TableRow) =>
                'isGroup' in record ? null : (
                    <span style={{fontSize: '12px'}}>
            {createdAt ? new Date(createdAt).toLocaleDateString() : ''}
          </span>
                ),
            width: 120,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            fixed: 'right' as 'right',
            render: (_, record: TableRow) => (
                'isGroup' in record ? null : (
                    <Space size="small" direction="vertical">
                        <Space size="small">
                            <Tooltip title="編輯">
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<EditOutlined/>}
                                    onClick={() => handleEdit(record as HKTVAccount)}
                                />
                            </Tooltip>
                            <Tooltip title="測試連線">
                                <Button
                                    size="small"
                                    icon={<LinkOutlined/>}
                                    onClick={() => handleTestConnection(record as HKTVAccount)}
                                    loading={loading}
                                    type={(record as HKTVAccount).status === 'connected' ? 'default' : 'dashed'}
                                />
                            </Tooltip>

                            <Popconfirm
                                title="確認刪除"
                                description={`確定要刪除賬號 "${(record as HKTVAccount).storeCode}" 嗎？此操作不可恢復。`}
                                onConfirm={() => handleDeleteAccount(record as HKTVAccount)}
                                okText="確認刪除"
                                cancelText="取消"
                                okType="danger"
                                placement="topLeft"
                            >
                                <Tooltip title="刪除">
                                    <Button
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined/>}
                                        style={{width: '100%'}}
                                    >
                                    </Button>
                                </Tooltip>
                            </Popconfirm>
                        </Space>

                        {/* 開關：帳號啟用/停用 */}
                        <Space size="small" style={{width: '100%', justifyContent: 'flex-end'}}>
                            <Tooltip title="啟用/停用此帳號">
                                <span style={{fontSize: 12, color: '#666'}}>啟用</span>
                            </Tooltip>
                            <Switch
                                checked={(record as HKTVAccount).isEnabled !== false}
                                size="small"
                                onChange={(checked) => handleToggleEnabled(checked, record as HKTVAccount)}
                            />
                        </Space>

                        {/* 開關：是否調用 /oapi/api/store */}
                        <Space size="small" style={{width: '100%', justifyContent: 'flex-end'}}>
                            <Tooltip title="調用 HKTV /oapi/api/store">
                                <span style={{fontSize: 12, color: '#666'}}>是否更新商店</span>
                            </Tooltip>
                   <Switch
                     checked={(record as HKTVAccount).isUpdateStore}
                     size="small"
                     onChange={(checked) => handleToggleStoreApi(checked, record as HKTVAccount)}
                   />
                        </Space>



                    </Space>
                )
            ),
        },
    ], [customers, loading, tagOptions]);

    // 根據列設置過濾和排序列 - 參考 Inbound 頁面實現
    const columns: ColumnsType<TableRow> = Object.entries(columnSettings)
        .filter(([_, config]: [string, any]) => config.visible)
        .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
        .map(([key]) => allColumns.find(col => col.key === key))
        .filter(Boolean) as ColumnsType<TableRow>;


    // 列設置下拉菜單 - 參考 Inbound 頁面實現
    const {Text} = Typography;
    const columnSettingsMenu = (
        <Menu>
            <Menu.Item key="header" disabled>
                <Text strong>列設置</Text>
            </Menu.Item>
            <Menu.Divider/>
            {Object.entries(columnSettings)
                .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
                .map(([key]) => (
                    <Menu.Item key={key}>
                        <Checkbox
                            checked={columnSettings[key as keyof ColumnSettings]?.visible ?? true}
                            onChange={(e) => handleColumnVisibilityChange(key as keyof ColumnSettings, e.target.checked)}
                        >
                            {key === 'storeCode' ? 'Store Code' :
                                key === 'status' ? 'Status' :
                                    key === 'warehouseIds' ? '倉庫IDs' :
                                        key === 'customerIds' ? '客戶' :
                                            key === 'apiKey' ? 'API Key' :
                                                key === 'privateKey' ? 'Private Key' :
                                                    key === 'lastConnected' ? 'Last Connected' :
                                                        key === 'createdAt' ? 'Created At' :
                                                            key === 'actions' ? 'Actions' : key}
                        </Checkbox>
                    </Menu.Item>
                ))}
            <Menu.Divider/>
            <Menu.Item key="showAll" onClick={showAllColumns}>
                <Button type="link" size="small" style={{padding: 0}}>
                    顯示所有列
                </Button>
            </Menu.Item>
            <Menu.Item key="reset" onClick={resetColumnSettings}>
                <Button type="link" size="small" style={{padding: 0}}>
                    重置列設置
                </Button>
            </Menu.Item>
        </Menu>
    );

    return (
        <div>
            <Card title="HKTVmall 連線帳號（Admin）">
                <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between'}}>
                    <Space>
                        <Search
                            placeholder="搜索 Store Code"
                            allowClear
                            style={{width: 250}}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<SearchOutlined/>}
                        />
                        <Select
                            placeholder="狀態篩選"
                            allowClear
                            style={{width: 120}}
                            value={statusFilter || undefined}
                            onChange={(value) => setStatusFilter(value || '')}
                        >
                            <Select.Option value="connected">已連線</Select.Option>
                            <Select.Option value="disconnected">未連線</Select.Option>
                        </Select>

                    </Space>

                    <Space>
                        <Space align="center">
                            <span style={{ fontSize: 12, color: '#666' }}>{allEnabled ? '一鍵斷開全部' : '一鍵連接全部'}</span>
                            <Switch
                                checked={allEnabled}
                                loading={loading}
                                disabled={!accounts.length}
                                onChange={(checked) => toggleAllEnabled(checked)}
                            />
                        </Space>
                        <Space align="center">
                            <span style={{ fontSize: 12, color: '#666' }}>{allUpdateStore ? '全部關閉更新商店' : '全部開啟更新商店'}</span>
                            <Switch
                                checked={allUpdateStore}
                                loading={loading}
                                disabled={!accounts.length}
                                onChange={(checked) => toggleAllUpdateStore(checked)}
                            />
                        </Space>
                        <Button
                            onClick={testAllConnections}
                            disabled={loading || !accounts.length}
                        >
                            一鍵測試所有連接
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleAddAccount}
                        >
                            新增帳號
                        </Button>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={fetchAccounts}
                            loading={loading}
                        >
                            刷新
                        </Button>
                        <Button
                            icon={<ExportOutlined/>}
                            onClick={handleExportCSV}
                        >
                            導出
                        </Button>
                        <Dropdown overlay={columnSettingsMenu} trigger={['click']}>
                            <Button icon={<SettingOutlined/>}>
                                列設置
                            </Button>
                        </Dropdown>
                    </Space>
                </div>
                <Table
                    rowKey="id"
                    dataSource={tableData}
                    columns={columns}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 條記錄`,
                    }}
                    onChange={handleTableChange}
                    scroll={{x: 1200}}

                />
            </Card>


            {/* 編輯模態框 */}
            <Modal
                title={`編輯帳號 - ${editingAccount?.storeCode}`}
                open={editModalVisible}
                onOk={handleSaveEdit}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingAccount(null);
                    form.resetFields();
                }}
                confirmLoading={loading}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{marginTop: 16}}
                >
                    <Form.Item label="Store Code">
                        <Input
                            value={editingAccount?.storeCode}
                            disabled
                            style={{backgroundColor: '#f5f5f5'}}
                        />
                    </Form.Item>


                    <Form.Item label="Status">
                        <Tag
                            icon={editingAccount?.status === 'connected' ? <CheckCircleOutlined/> :
                                <CloseCircleOutlined/>}
                            color={editingAccount?.status === 'connected' ? 'green' : 'default'}
                        >
                            {editingAccount?.status === 'connected' ? '已連線' : '未連線'}
                        </Tag>
                    </Form.Item>

                    <Form.Item
                        name="mmsLogin"
                        label="MMS Login"
                    >
                        <Input
                            placeholder="請輸入 MMS Login"
                            autoComplete="off"
                        />
                    </Form.Item>

                    <Form.Item
                        name="mmsPassword"
                        label="MMS Password"
                    >
                        <Input
                            placeholder="請輸入 MMS Password"
                            autoComplete="off"
                        />
                    </Form.Item>

                    <Form.Item
                        name="apiKey"
                        label="API Key"
                        rules={[
                            {required: true, message: '請輸入 API Key'},
                            {min: 10, message: 'API Key 長度至少 10 個字符'}
                        ]}
                    >
                        <Input
                            placeholder="請輸入 API Key"
                            autoComplete="off"
                        />
                    </Form.Item>

                    <Form.Item
                        name="privateKey"
                        label="Private Key"
                        rules={[
                            {required: true, message: '請輸入 Private Key'},
                            {min: 10, message: 'Private Key 長度至少 10 個字符'}
                        ]}
                    >
                        <Input.TextArea
                            placeholder="請輸入 Private Key（支持 PEM 格式多行文本）"
                            autoComplete="off"
                            rows={6}
                            style={{fontFamily: 'monospace', fontSize: '12px'}}
                        />
                    </Form.Item>

                    <Form.Item name="warehouseIds" label="倉庫IDs">
                        <Input placeholder="請輸入多個倉庫ID，逗號分隔，例如：10,20,30（可選）"/>
                    </Form.Item>

                    <Form.Item 
                        name="customerIds" 
                        label="客戶"
                        tooltip="可以選擇多個客戶，一個 Store Code 可以關聯多個客戶"
                    >
                        <Select
                            mode="multiple"
                            placeholder="請選擇客戶（可多選）"
                            showSearch
                            allowClear
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                const text = Array.isArray(children) ? children.join('') : String(children || '');
                                return text.toLowerCase().includes(input.toLowerCase());
                            }}
                            style={{ width: '100%' }}
                        >
                            {customers.map(customer => (
                                <Select.Option key={customer.id} value={customer.id}>
                                    {customer.name}{customer.email && ` (${customer.email})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={true}
                    >
                        {({ getFieldValue }) => {
                            const warehouseIds = getFieldValue('warehouseIds');
                            console.log('📦 [Edit Form] Current warehouseIds value:', warehouseIds);
                            
                            const warehouseList = warehouseIds 
                                ? warehouseIds.split(',').map((id: string) => id.trim()).filter(Boolean)
                                : [];
                            
                            console.log('📦 [Edit Form] Parsed warehouse list:', warehouseList);
                            console.log('📦 [Edit Form] Warehouse list length:', warehouseList.length);

                            if (warehouseList.length > 0) {
                                // 有 warehouse IDs - 顯示 Tabs
                                console.log('✅ [Edit Form] Rendering TABS mode with tabs:', warehouseList);
                                return (
                                    <Form.Item label="Tags（按倉庫ID分類）">
                                        <Tabs type="card" defaultActiveKey={warehouseList[0]}>
                                            {warehouseList.map((warehouseId: string) => (
                                                <Tabs.TabPane tab={`倉庫 ${warehouseId}`} key={warehouseId}>
                                                    <Form.Item name={`tags_${warehouseId}`} noStyle>
                                                        <Select 
                                                            mode="tags" 
                                                            placeholder={`為倉庫 ${warehouseId} 輸入標籤`}
                                                            allowClear
                                                            style={{ width: '100%' }}
                                                        >
                                                            {tagOptionsForSelect}
                                                        </Select>
                                                    </Form.Item>
                                                </Tabs.TabPane>
                                            ))}
                                        </Tabs>
                                    </Form.Item>
                                );
                            } else {
                                // 沒有 warehouse IDs - 顯示普通的 Tags 輸入
                                console.log('✅ [Edit Form] Rendering NORMAL mode');
                                return (
                                    <Form.Item name="tags" label="Tags（屬於 Store Code）">
                                        <Select 
                                            mode="tags" 
                                            placeholder="輸入後按 Enter 添加或選擇現有標籤" 
                                            allowClear
                                        >
                                            {tagOptionsForSelect}
                                        </Select>
                                    </Form.Item>
                                );
                            }
                        }}
                    </Form.Item>

                </Form>
            </Modal>

            {/* 新增帳號模態框 */}
            <Modal
                title="新增 HKTVmall 帳號"
                open={addModalVisible}
                onOk={handleSaveAdd}
                onCancel={() => {
                    setAddModalVisible(false);
                    addForm.resetFields();
                }}
                confirmLoading={loading}
                width={600}
            >
                <Form
                    form={addForm}
                    layout="vertical"
                    style={{marginTop: 16}}
                >
                    <Form.Item
                        name="storeCode"
                        label="Store Code"
                        rules={[
                            {required: true, message: '請輸入 Store Code'},
                            {
                                pattern: /^[A-Za-z0-9]+$/,
                                message: 'Store Code 只能包含字母和數字'
                            },
                            {min: 5, message: 'Store Code 長度至少 5 個字符'},
                            {max: 20, message: 'Store Code 長度不能超過 20 個字符'}
                        ]}
                    >
                        <Input
                            placeholder="請輸入 Store Code（如：H5980001）"
                            style={{textTransform: 'uppercase'}}
                            onChange={(e) => {
                                // 自動轉換為大寫
                                addForm.setFieldsValue({
                                    storeCode: e.target.value.toUpperCase()
                                });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="apiKey"
                        label="API Key"
                        rules={[
                            {required: true, message: '請輸入 API Key'},
                            {min: 10, message: 'API Key 長度至少 10 個字符'}
                        ]}
                    >
                        <Input
                            placeholder="請輸入 API Key"
                            autoComplete="off"
                        />
                    </Form.Item>

                    <Form.Item
                        name="privateKey"
                        label="Private Key"
                        rules={[
                            {required: true, message: '請輸入 Private Key'},
                            {min: 10, message: 'Private Key 長度至少 10 個字符'}
                        ]}
                    >
                        <Input.TextArea
                            placeholder="請輸入 Private Key（支持 PEM 格式多行文本）"
                            autoComplete="off"
                            rows={6}
                            style={{fontFamily: 'monospace', fontSize: '12px'}}
                        />
                    </Form.Item>

                    <Form.Item name="warehouseIds" label="倉庫IDs">
                        <Input 
                            placeholder="請輸入多個倉庫ID，逗號分隔，例如：B04200191,B04200192"
                            onChange={(e) => {
                                console.log('📝 Warehouse IDs input changed:', e.target.value);
                            }}
                        />
                    </Form.Item>

                    <Form.Item 
                        name="customerIds" 
                        label="客戶"
                        tooltip="可以選擇多個客戶，一個 Store Code 可以關聯多個客戶"
                    >
                        <Select
                            mode="multiple"
                            placeholder="請選擇客戶（可多選）"
                            showSearch
                            allowClear
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                const children = option?.children;
                                const text = Array.isArray(children) ? children.join('') : String(children || '');
                                return text.toLowerCase().includes(input.toLowerCase());
                            }}
                            style={{ width: '100%' }}
                        >
                            {customers.map(customer => (
                                <Select.Option key={customer.id} value={customer.id}>
                                    {customer.name}{customer.email && ` (${customer.email})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={true}
                    >
                        {({ getFieldValue }) => {
                            const warehouseIds = getFieldValue('warehouseIds');
                            console.log('📦 [Add Form] warehouseIds value:', warehouseIds);
                            
                            const warehouseList = warehouseIds 
                                ? warehouseIds.split(',').map((id: string) => id.trim()).filter(Boolean)
                                : [];
                            
                            console.log('📦 [Add Form] warehouse list:', warehouseList);

                            if (warehouseList.length > 0) {
                                // 有 warehouse IDs - 顯示 Tabs
                                console.log('✅ [Add Form] Rendering TABS mode with', warehouseList.length, 'tabs');
                                return (
                                    <Form.Item label="Tags（按倉庫ID分類）">
                                        <Tabs type="card" defaultActiveKey={warehouseList[0]}>
                                            {warehouseList.map((warehouseId: string) => (
                                                <Tabs.TabPane tab={`倉庫 ${warehouseId}`} key={warehouseId}>
                                                    <Form.Item name={`tags_${warehouseId}`} noStyle>
                                                        <Select 
                                                            mode="tags" 
                                                            placeholder={`為倉庫 ${warehouseId} 輸入標籤`}
                                                            allowClear
                                                            style={{ width: '100%' }}
                                                        >
                                                            {tagOptionsForSelect}
                                                        </Select>
                                                    </Form.Item>
                                                </Tabs.TabPane>
                                            ))}
                                        </Tabs>
                                    </Form.Item>
                                );
                            } else {
                                // 沒有 warehouse IDs - 顯示普通的 Tags 輸入
                                console.log('✅ [Add Form] Rendering NORMAL mode');
                                return (
                                    <Form.Item name="tags" label="Tags（屬於 Store Code）">
                                        <Select 
                                            mode="tags" 
                                            placeholder="輸入後按 Enter 新增或選擇現有標籤" 
                                            allowClear
                                        >
                                            {tagOptionsForSelect}
                                        </Select>
                                    </Form.Item>
                                );
                            }
                        }}
                    </Form.Item>

                    <Alert
                        message="提示"
                        description="新創建的帳號狀態將設為「未連線」，創建後可以使用測試連線功能驗證配置是否正確。如果輸入了倉庫IDs（逗號分隔），下方會自動顯示對應的標籤分頁。"
                        type="info"
                        showIcon
                        style={{marginTop: 16}}
                    />
                </Form>
            </Modal>

        </div>
    );
};

export default HKTVmallAdminAccounts;


